import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { reRunAuthOnFileCheck } from "../../../api/postCall/reRunAuthCheck";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { GeneralOrderDetails } from "../../../data-model/medicalPaOrdersModel";
import { UserGeneralInfoContextKeys } from "../../../enums/userGeneralInfoContextKeys";
import { RootState } from "../../../redux/store/store";
import { UserStore } from "../../../store/UserGeneralInfoStore";
import { logError } from "../../../utils/customLogger";
import { logRefreshButtonClicked } from "../utils/headerEventLogger";
interface UseAuthOnFileRefreshProps {
  /**
   * Explicit order ID. Takes precedence over `generalOrderDetails.order_id` for
   * both the `reRunAuthOnFileCheck` payload and the post-refresh `fetchAll`
   * call. Lets call sites that don't have the full order object (e.g. the
   * single-order header) still drive the refresh correctly.
   */
  orderId?: string;
  /** Patient MRN; falls back to `generalOrderDetails.demographics.patient_id` then `patient_mrn`. */
  patientMrn?: string;
  /** Drug / regimen name; falls back to `generalOrderDetails.payload.regimen_name`. */
  drugName?: string;
  /** Full order details object; used as the fallback source for any field not passed explicitly. */
  generalOrderDetails?: GeneralOrderDetails;
  /** Callback invoked after a successful auth-on-file refresh. */
  onSuccess?: () => void;
  /** Callback invoked when an auth-on-file refresh fails. */
  onError?: (error: Error) => void;
}

/**
 * Provides a throttled callback that re-runs the auth-on-file check for the
 * current order, surfaces success/error toasts, re-fetches all data domains
 * via the accumulator context, and invokes optional `onSuccess` / `onError`
 * callbacks.
 *
 * Field resolution order for the request payload:
 *   - `patient_id`       : `patientMrn` -> `generalOrderDetails.demographics.patient_id` -> `patient_mrn` -> ""
 *   - `regimen_name`     : `drugName` -> `generalOrderDetails.payload.regimen_name` -> ""
 *   - `order_id`         : `orderId` -> `generalOrderDetails.order_id` -> ""
 *   - `date_of_service`  : `generalOrderDetails.payload.date_of_service` -> ""
 *
 * The post-refresh `fetchAll` uses the same precedence for `order_id`.
 *
 * @param props - See {@link UseAuthOnFileRefreshProps}
 * @returns `handleAuthOnFileRefresh` callback and `isRefreshing` flag
 */
export const useAuthOnFileRefresh = ({
  orderId,
  patientMrn,
  drugName,
  generalOrderDetails,
  onSuccess,
  onError,
}: UseAuthOnFileRefreshProps) => {
  const { fetchAll } = useDataAccumulatorForSingleOrder();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastExecutionTimeRef = useRef<number>(0);

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const selectedOrganization =
    UserStore.getItem(UserGeneralInfoContextKeys.HEALTHCARE_FACILITY_ID) ?? "";

  const handleAuthOnFileRefresh = async () => {
    const currentTime = Date.now();
    const timeSinceLastExecution = currentTime - lastExecutionTimeRef.current;

    // Check if 10 seconds (10000ms) have passed since last execution
    if (timeSinceLastExecution < 10000) {
      toast.error(ToastMessages.RETRY_ERROR);
      return;
    }

    lastExecutionTimeRef.current = currentTime;
    setIsRefreshing(true);
    try {
      await Promise.race([
        reRunAuthOnFileCheck({
          patient_id:
            patientMrn ??
            generalOrderDetails?.demographics?.patient_id ??
            generalOrderDetails?.demographics?.patient_mrn ??
            "",
          regimen_name:
            drugName ?? generalOrderDetails?.payload?.regimen_name ?? "",
          order_id: orderId ?? generalOrderDetails?.order_id ?? "",
          date_of_service: generalOrderDetails?.payload?.date_of_service ?? "",
          org_id: selectedOrganization ?? "",
        }),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
      logRefreshButtonClicked({
        generalOrderDetails,
        user,
        payerName,
      });
      const resolvedOrderId = orderId ?? generalOrderDetails?.order_id ?? "";
      if (resolvedOrderId && selectedOrganization) {
        await fetchAll(resolvedOrderId, selectedOrganization);
      }
      toast.success(ToastMessages.AUTH_ON_FILE_REFRESHED_SUCCESS);
      onSuccess?.();
    } catch (error) {
      logError(error as Error);
      toast.error(ToastMessages.AUTH_ON_FILE_REFRESHED_FAILURE);
      onError?.(error as Error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    handleAuthOnFileRefresh,
    isRefreshing,
  };
};
